import Phaser from 'phaser';

export const VisualTheme = {
  colors: {
    ink: 0x090d12,
    night: 0x111822,
    slate: 0x1b2633,
    panel: 0x243142,
    panelHover: 0x35465c,
    panelPressed: 0x17202b,
    gold: 0xb78f46,
    goldBright: 0xe3c477,
    parchment: 0xf6e6bd,
    parchmentMuted: 0xd8c69f,
    jade: 0x3f7f61,
    jadeBright: 0x69a982,
    crimson: 0x873f42,
    ocean: 0x18384c,
    oceanLight: 0x2d5d70,
    shadow: 0x030507,
  },
  text: {
    title: '#f6e6bd',
    accent: '#e3c477',
    body: '#eadfca',
    muted: '#b8aa90',
    success: '#8bc69d',
    danger: '#e18a84',
  },
  fonts: {
    display: 'Georgia, Cambria, serif',
    body: 'Arial, Helvetica, sans-serif',
  },
} as const;

export interface PanelOptions {
  fill?: number;
  alpha?: number;
  stroke?: number;
  strokeAlpha?: number;
  radius?: number;
  shadow?: boolean;
}

export function addPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  options: PanelOptions = {},
) {
  const radius = options.radius ?? 12;
  const container = scene.add.container(x, y);

  if (options.shadow !== false) {
    const shadow = scene.add.rectangle(6, 8, width, height, VisualTheme.colors.shadow, 0.5).setOrigin(0.5);
    shadow.setStrokeStyle(0);
    container.add(shadow);
  }

  const panel = scene.add.rectangle(0, 0, width, height, options.fill ?? VisualTheme.colors.night, options.alpha ?? 0.94).setOrigin(0.5);
  panel.setStrokeStyle(2, options.stroke ?? VisualTheme.colors.gold, options.strokeAlpha ?? 0.9);
  container.add(panel);

  const inner = scene.add.rectangle(0, 0, Math.max(1, width - 12), Math.max(1, height - 12), 0x000000, 0).setOrigin(0.5);
  inner.setStrokeStyle(1, VisualTheme.colors.goldBright, 0.18);
  container.add(inner);

  // Phaser rectangles do not expose corner radius, so a subtle corner ornament gives
  // every panel the same carved-frame silhouette without requiring external UI art.
  const ornamentSize = Math.min(18, radius + 6);
  const corners = [
    [-width / 2 + ornamentSize, -height / 2 + ornamentSize],
    [width / 2 - ornamentSize, -height / 2 + ornamentSize],
    [-width / 2 + ornamentSize, height / 2 - ornamentSize],
    [width / 2 - ornamentSize, height / 2 - ornamentSize],
  ];
  corners.forEach(([cornerX, cornerY]) => {
    container.add(scene.add.circle(cornerX, cornerY, 2.2, VisualTheme.colors.goldBright, 0.72));
  });

  return container;
}

export function addBackdropShade(scene: Phaser.Scene, alpha = 0.34) {
  const { width, height } = scene.scale;
  return scene.add.rectangle(width / 2, height / 2, width, height, VisualTheme.colors.ink, alpha);
}

export function addSectionHeading(scene: Phaser.Scene, x: number, y: number, text: string, size = 24) {
  return scene.add.text(x, y, text, {
    fontFamily: VisualTheme.fonts.display,
    fontSize: `${size}px`,
    color: VisualTheme.text.title,
    stroke: '#21140b',
    strokeThickness: 2,
  });
}
